import { useToastStore } from "&/toast";
import { AnimatePresence, motion } from "framer-motion";
import { ReactNode, useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { twMerge } from "tailwind-merge";

interface DialogProps {
  visible?: boolean;
  children?: ReactNode;
  persist?: boolean;
  onClose?: (value?: boolean) => unknown;
}
export const Dialog = ({
  visible,
  children,
  persist = true,
  onClose,
}: DialogProps) => {
  const { add } = useToastStore();
  const [shake, setShake] = useState(false);
  const handleCancel = useCallback(() => {
    if (persist) return setShake(true);
    onClose?.();
  }, [persist, onClose]);
  const animationEnd = useCallback(() => {
    setShake(false);
    add("팝업내용을 확인해주세요.");
  }, [add]);
  useEffect(() => {
    if (visible) {
      // 현재 스크롤 위치 저장
      const scrollPosition = window.scrollY;
      const scrollbarWidth =
        window.innerWidth - document.documentElement.clientWidth;

      // body 스크롤 막기
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollPosition}px`;
      document.body.style.width = "100%";
      // 스크롤바 너비만큼 padding 추가 (레이아웃 시프트 방지)
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }

      // html에도 스크롤 막기 (모바일 대응)
      document.documentElement.style.overflow = "hidden";
      document.documentElement.style.position = "fixed";
      document.documentElement.style.width = "100%";

      // 스크롤 위치 복원 함수
      const restoreScroll = () => {
        document.body.style.overflow = "";
        document.body.style.position = "";
        document.body.style.top = "";
        document.body.style.width = "";
        document.body.style.paddingRight = "";
        document.documentElement.style.overflow = "";
        document.documentElement.style.position = "";
        document.documentElement.style.width = "";
        window.scrollTo(0, scrollPosition);
      };

      // 스크롤 위치 복원을 위해 visible이 false일 때 복원 함수 실행
      return () => {
        restoreScroll();
      };
    }
  }, [visible]);
  return createPortal(
    <AnimatePresence>
      {visible && (
        <>
          <motion.i
            onClick={handleCancel}
            className="fixed inset-0 z-[9999] bg-black opacity-0 bg-opacity-15 last-of-type:opacity-100"
            initial={{
              opacity: 0,
              pointerEvents: "none",
            }}
            animate={{
              pointerEvents: "auto",
              opacity: 1,
            }}
            exit={{
              pointerEvents: "none",
              opacity: 0,
            }}
          />

          <motion.div
            onAnimationEnd={animationEnd}
            className={twMerge(
              "bg-white w-[calc(100%-40px)] max-w-[440px] shadow-xl rounded-2xl p-4 fixed z-[9999] inset-0 h-fit m-auto origin-center",
              shake && "animate-shake"
            )}
            transition={{
              type: "spring",
              stiffness: 300,
              damping: 20,
            }}
            initial={{
              pointerEvents: "none",
              opacity: 0,
              transform: "scale(0)",
            }}
            animate={{
              pointerEvents: "auto",
              opacity: 1,
              transform: "scale(1)",
            }}
            exit={{
              pointerEvents: "none",
              opacity: 0,
              transform: "scale(0)",
            }}
          >
            {children}
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};
